import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Check, Settings } from 'lucide-react';
import { Button } from './ui/button';

const CONSENT_KEY = 'zampa_cookie_consent';

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      // Piccolo ritardo per non coprire subito la pagina
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const saveConsent = (type) => {
    const consent = {
      accepted: type,
      analytics: type === 'all' ? true : prefs.analytics,
      date: new Date().toISOString(),
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:max-w-md z-[9999]"
          role="dialog"
          aria-label="Preferenze cookie"
          data-testid="cookie-banner"
        >
          <div className="bg-[var(--color-deep)] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 p-5 pb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[var(--color-sage)] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Cookie size={15} className="text-white" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Gestione dei Cookie</p>
                  <p className="text-white/50 text-xs">Una Zampa in Paradiso</p>
                </div>
              </div>
              <button
                onClick={() => saveConsent('necessary')}
                className="text-white/40 hover:text-white/70 transition-colors mt-0.5"
                aria-label="Chiudi e accetta solo necessari"
                data-testid="cookie-banner-close"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="px-5 pb-4">
              <p className="text-white/70 text-xs leading-relaxed">
                Utilizziamo cookie tecnici necessari e, previo consenso, cookie analitici per migliorare il servizio. Per saperne di più, consulta la{' '}
                <Link to="/cookie-policy" className="text-[var(--color-sage)] hover:underline font-medium">Cookie Policy</Link>
                {' '}e la{' '}
                <Link to="/privacy" className="text-[var(--color-sage)] hover:underline font-medium">Privacy Policy</Link>.
              </p>

              {/* Dettagli toggle */}
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  className="mt-3 space-y-2"
                >
                  <label className="flex items-center justify-between p-2.5 bg-white/5 rounded-xl cursor-not-allowed">
                    <div>
                      <p className="text-white text-xs font-medium">Cookie Tecnici</p>
                      <p className="text-white/45 text-[11px]">Necessari per il funzionamento del sito</p>
                    </div>
                    <div className="w-8 h-4 bg-[var(--color-sage)] rounded-full relative flex-shrink-0">
                      <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-white rounded-full" />
                    </div>
                  </label>
                  <label className="flex items-center justify-between p-2.5 bg-white/5 rounded-xl cursor-pointer" onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}>
                    <div>
                      <p className="text-white text-xs font-medium">Cookie Analitici</p>
                      <p className="text-white/45 text-[11px]">Statistiche anonime di navigazione</p>
                    </div>
                    <div className={`w-8 h-4 rounded-full relative flex-shrink-0 transition-colors ${prefs.analytics ? 'bg-[var(--color-sage)]' : 'bg-white/20'}`}>
                      <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-all ${prefs.analytics ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </label>
                </motion.div>
              )}

              {/* Bottoni */}
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <Button
                  onClick={() => saveConsent('all')}
                  size="sm"
                  className="flex-1 bg-[var(--color-sage)] hover:bg-[var(--color-sage-dark)] text-white rounded-xl text-xs font-semibold h-9"
                  data-testid="cookie-accept-all"
                >
                  <Check size={13} className="mr-1" /> Accetta tutti
                </Button>
                <Button
                  onClick={() => showDetails ? saveConsent('custom') : saveConsent('necessary')}
                  variant="outline"
                  size="sm"
                  className="flex-1 border-white/20 text-white hover:bg-white/10 rounded-xl text-xs h-9"
                  data-testid="cookie-accept-necessary"
                >
                  Solo necessari
                </Button>
                <Button
                  onClick={() => setShowDetails(d => !d)}
                  variant="ghost"
                  size="sm"
                  className="sm:flex-none text-white/50 hover:text-white hover:bg-white/5 rounded-xl text-xs h-9 px-2"
                  data-testid="cookie-settings-btn"
                  title="Personalizza"
                >
                  <Settings size={14} />
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
